import React, { useEffect, useState } from 'react';
import { getAllRecipes } from '../services/api';
import RecipeCard from '../components/RecipeCard';
import '../styles/Explore.css';

const Explore = () => {
    const [recipes, setRecipes] = useState([]);
    const [filteredRecipes, setFilteredRecipes] = useState([]);
    const [error, setError] = useState(null);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0); // 0 for All, 1 for Collections, 2 for Search, 3 for Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCookTime, setSelectedCookTime] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    const tabs = ['All', 'Collections', 'Search', 'Filters'];

    // Minimalistic search icon SVG
    const SearchIcon = () => (
        <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className="search-icon-svg"
        >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
        </svg>
    );

    useEffect(() => {
        async function fetchRecipes() {
            try {
                setLoading(true);
                const data = await getAllRecipes();
                setRecipes(data);
                setFilteredRecipes(data);
                // Extract unique categories
                const uniqueCategories = [...new Set(data.map(recipe => recipe.category))];
                setCategories(uniqueCategories);
            } catch (err) {
                console.error('Failed to fetch recipes:', err);
                setError('Failed to fetch recipes. Please try again later.');
            } finally {
                setLoading(false);
            }
        }
        fetchRecipes();
    }, []);

    const handleSearch = (searchTerm) => {
        const filtered = recipes.filter(recipe =>
            recipe.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            recipe.ingredients.some(ingredient =>
                ingredient.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredRecipes(filtered);
    };

    const handleFilter = ({ category, cookTime }) => {
        let filtered = [...recipes];

        if (category) {
            filtered = filtered.filter(recipe => recipe.category === category);
        }

        if (cookTime) {
            if (cookTime === 'under_30') {
                filtered = filtered.filter(recipe => recipe.cooking_time < 30);
            } else if (cookTime === '30_60') {
                filtered = filtered.filter(recipe => recipe.cooking_time >= 30 && recipe.cooking_time <= 60);
            } else if (cookTime === 'over_60') {
                filtered = filtered.filter(recipe => recipe.cooking_time > 60);
            }
        }

        setFilteredRecipes(filtered);
    };

    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        if (tabIndex === 0) {
            // Reset all filters when switching to All
            setSearchTerm('');
            setSelectedCategory('');
            setSelectedCookTime('');
            setFilteredRecipes(recipes);
        }
    };

    const handleSearchTabClick = () => {
        if (activeTab === 2) {
            // If search tab is already active, toggle expansion
            setIsSearchExpanded(!isSearchExpanded);
        } else {
            // If switching to search tab, make it active but don't expand
            setActiveTab(2);
            setIsSearchExpanded(false);
        }
    };

    const handleFilterTabClick = () => {
        setActiveTab(3);
        setIsSearchExpanded(false);
    };

    if (loading) {
        return (
            <div className="explore-container">
                <div className="text-center py-2">
                    <h2>Loading recipes…</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="explore-container">
            {error && <div className="error-message compact">{error}</div>}

            <div className="explore-tabs-wrapper">
                <nav className="explore-tabs">
                    {/* Left group: All and Collections */}
                    <div className="tab-group-left">
                        <button
                            className={`explore-tab ${activeTab === 0 ? 'active' : ''}`}
                            onClick={() => handleTabChange(0)}
                        >
                            All
                        </button>
                        <button
                            className={`explore-tab ${activeTab === 1 ? 'active' : ''}`}
                            onClick={() => handleTabChange(1)}
                        >
                            Collections
                        </button>
                    </div>

                    {/* Right group: Search and Filters */}
                    <div className="tab-group-right">
                        <button
                            className={`explore-tab ${activeTab === 2 ? 'active' : ''} ${isSearchExpanded ? 'expanded' : ''}`}
                            onClick={handleSearchTabClick}
                        >
                            <span className="search-icon">
                                <SearchIcon />
                            </span>
                            {isSearchExpanded && (
                                <input
                                    type="text"
                                    placeholder="Search recipes..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearch(e.target.value);
                                    }}
                                    className="search-input-inline"
                                    autoFocus
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}
                        </button>
                        <button
                            className={`explore-tab ${activeTab === 3 ? 'active' : ''}`}
                            onClick={handleFilterTabClick}
                        >
                            Filters
                        </button>
                    </div>
                </nav>
                
                <div className="explore-content">
                    {/* Recipe Grid - shown for All, Search, and Filters tabs */}
                    {(activeTab === 0 || activeTab === 2 || activeTab === 3) && (
                        <div className="recipes-section">
                            {/* Filter controls - only shown in Filters tab */}
                            {activeTab === 3 && (
                                <div className="filter-controls">
                                    <select
                                        value={selectedCategory}
                                        onChange={(e) => {
                                            setSelectedCategory(e.target.value);
                                            handleFilter({ category: e.target.value, cookTime: selectedCookTime });
                                        }}
                                        className="filter-select"
                                    >
                                        <option value="">All Categories</option>
                                        {categories.map((category) => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>

                                    <select
                                        value={selectedCookTime}
                                        onChange={(e) => {
                                            setSelectedCookTime(e.target.value);
                                            handleFilter({ category: selectedCategory, cookTime: e.target.value });
                                        }}
                                        className="filter-select"
                                    >
                                        <option value="">All Cooking Times</option>
                                        <option value="under_30">Under 30 minutes</option>
                                        <option value="30_60">30-60 minutes</option>
                                        <option value="over_60">Over 60 minutes</option>
                                    </select>
                                </div>
                            )}

                            {/* Recipe Grid */}
                            <div className="recipes-grid">
                                {filteredRecipes.length > 0 ? (
                                    filteredRecipes.map((recipe) => (
                                        <RecipeCard 
                                            key={recipe.id} 
                                            recipe={recipe} 
                                            variant="minimal"
                                        />
                                    ))
                                ) : (
                                    <div className="no-recipes-message">
                                        <h3>No recipes found</h3>
                                        <p>Try adjusting your search or filter criteria to find more recipes.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 1 && (
                        <div className="collections-section">
                            <h2>Collections</h2>
                            <p>Your recipe collections will appear here.</p>
                            {/* Add Collections component here when available */}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Explore;